/**
 * ts-specs.js - renders the tabbed product info panel.
 *
 * Reads the handle out of the URL, looks it up in SPECS below, and injects a
 * Specs / In The Box / Care panel into the product info column. Silent no-op
 * on any page that is not a product, or any product without an entry.
 *
 * SPECS lives in this file on purpose. It used to be a separate module
 * imported by relative path, but relative imports resolve to the UNVERSIONED
 * asset URL, which Shopify's CDN caches for a long time; edits to the data
 * file never reached shoppers (seen 2026-09-07 after the Eyes Up Trainer
 * rename). This file is loaded through `asset_url`, so it gets a fresh
 * version param on every save. ts-specs-data.js is kept only as a fallback
 * copy and is no longer imported.
 */

const SPECS = {
  'eyes-up-soccer-dribbling-trainer': {
    tagline: 'Head-mounted hand blocker for vision training',
    specs: [
      ['Blocker size', '20 cm wide, 19.5 cm drop'],
      ['Blocker material', 'Flexible moulded silicone'],
      ['Ventilation', 'Perforated hole grid across the palm'],
      ['Head strap', 'Adjustable elastic, 16 cm + 10 cm'],
      ['Crown strap', '7 cm, adjustable'],
      ['Strap width', '4 cm'],
      ['Closure', 'Slider buckle with centre clip'],
      ['Fits', 'Youth through adult'],
      ['Sports', 'Basketball, soccer, lacrosse, handball']
    ],
    box: ['1 x Eyes Up Trainer headband', '1 x Hand blocker panel, attached'],
    care: 'Wipe with a damp cloth and air dry. Do not machine wash.',
    faq: [
      [
        'It feels weird. Is that normal?',
        'Yes. That is the point. The discomfort means your brain is being forced to adjust. Most people stop noticing it after 3 to 5 sessions. Push through the first week and you will feel the difference in games.'
      ],
      [
        'How do I get the fit right?',
        'Take 2 minutes before your first session to adjust the straps. The head strap should sit snug but not tight. The crown strap keeps the blocker from sliding forward. Once it is dialed in it stays put. Do not skip the adjustment step.'
      ],
      [
        'What sports can I use this for?',
        'Any sport where someone gets in your face. Basketball, soccer, lacrosse, field hockey, handball. If a defender can get in your vision, this trains you to see past them.'
      ],
      [
        'How long until I see results?',
        'Most players notice a difference in 1 to 2 weeks of regular practice. Your hands and feet start trusting feel over sight. Your head stays up naturally.'
      ]
    ]
  },

  'basketball-drawstring-backpack-with-shoe-compartment': {
    tagline: 'Drawstring gym bag with wet and dry separation',
    specs: [
      ['Dimensions', '45 x 33 x 16 cm'],
      ['Capacity', '25 L'],
      ['Material', 'Water-resistant nylon'],
      ['Shoe compartment', 'Separate zippered base section'],
      ['Wet and dry', 'Base keeps damp kit off the main bag'],
      ['Side pockets', 'Mesh, both sides'],
      ['Visibility', 'Reflective stripes down the front'],
      ['Closure', 'Drawstring top with shoulder straps']
    ],
    box: ['1 x Drawstring backpack'],
    care: 'Spot clean and air dry. Do not tumble dry.'
  },

  'basketball-padded-arm-sleeve-elbow-guard': {
    tagline: 'Compression sleeve with honeycomb elbow padding',
    specs: [
      ['Padding', 'Honeycomb cell foam over the elbow'],
      ['Fabric', 'Nylon and spandex compression knit'],
      ['Grip', 'Silicone band at the upper opening'],
      ['Coverage', 'Bicep to forearm'],
      ['Breathability', 'Moisture-wicking, quick dry'],
      ['Sizes', 'S, M, L, XL'],
      ['Sold as', 'Single sleeve, fits either arm']
    ],
    box: ['1 x Padded arm sleeve'],
    care: 'Machine wash cold, hang dry. No bleach or ironing.'
  },

  'massage-gun-handheld-muscle-recovery': {
    tagline: 'Percussion massager for post-game recovery',
    specs: [
      ['Heads', '4 interchangeable attachments'],
      ['Display', 'LED speed and battery readout'],
      ['Charging', 'USB-C'],
      ['Motor', 'Quiet brushless'],
      ['Body', 'Handheld, lightweight'],
      ['Use on', 'Quads, calves, shoulders, back']
    ],
    box: ['1 x Massage gun', '4 x Massage heads', '1 x USB-C charging cable'],
    care: 'Wipe the body clean. Do not submerge. Charge fully before first use.'
  },

  'basketball-shoe-grip-spray': {
    tagline: 'Traction spray that revives worn soles',
    specs: [
      ['Volume', '120 ml'],
      ['Works on', 'Any rubber sneaker sole'],
      ['Effect', 'Immediate, no drying wait'],
      ['Best for', 'Court shoes with worn tread'],
      ['Lasts', 'Months of regular use per bottle']
    ],
    box: ['1 x 120 ml grip spray bottle'],
    care: 'Spray onto the sole, not the court. Store upright away from heat.'
  },

  'basketball-mesh-ball-carry-bag': {
    tagline: 'Mesh sling for carrying a ball hands-free',
    specs: [
      ['Fits', 'One basketball, soccer ball or volleyball'],
      ['Material', 'Breathable mesh'],
      ['Pocket', 'Small zippered pocket'],
      ['Strap', 'Adjustable shoulder strap'],
      ['Carry', 'Over the shoulder or across the back']
    ],
    box: ['1 x Mesh ball carry bag'],
    care: 'Rinse and air dry.'
  }
};

function handleFromUrl() {
  const m = location.pathname.match(/\/products\/([^/?#]+)/);
  return m ? m[1] : null;
}

function rows(pairs) {
  return '<dl class="ts-info__grid">' +
    pairs.map(([k, v]) => '<dt>' + k + '</dt><dd>' + v + '</dd>').join('') +
    '</dl>';
}

function list(items) {
  return '<ul class="ts-info__list">' +
    items.map((i) => '<li>' + i + '</li>').join('') +
    '</ul>';
}

function faqHtml(items) {
  return '<dl class="ts-info__faq">' +
    items.map(([q, a]) =>
      '<dt class="ts-info__faq-q">' + q + '</dt>' +
      '<dd class="ts-info__faq-a">' + a + '</dd>'
    ).join('') +
    '</dl>';
}

function build(data) {
  const host =
    document.querySelector('.product__info-container') ||
    document.querySelector('.product__info-wrapper');
  if (!host || document.querySelector('.ts-info')) return;

  const panels = [
    { id: 'specs', label: 'Specs', html: rows(data.specs) },
    { id: 'box', label: 'In The Box', html: list(data.box) },
    { id: 'care', label: 'Care', html: '<p class="ts-info__note">' + data.care + '</p>' }
  ];

  if (data.faq) {
    panels.push({ id: 'faq', label: 'FAQ', html: faqHtml(data.faq) });
  }

  const el = document.createElement('div');
  el.className = 'ts-info';
  el.innerHTML =
    '<p class="ts-info__tagline">' + data.tagline + '</p>' +
    '<div class="ts-info__tabs" role="tablist">' +
      panels.map((p, i) =>
        '<button class="ts-info__tab" role="tab" data-t="' + p.id + '"' +
        (i === 0 ? ' data-on aria-selected="true"' : ' aria-selected="false"') +
        '>' + p.label + '</button>').join('') +
    '</div>' +
    panels.map((p, i) =>
      '<div class="ts-info__panel" role="tabpanel" data-p="' + p.id + '"' +
      (i === 0 ? ' data-on' : '') + '>' + p.html + '</div>').join('');

  const anchor = host.querySelector('.product-form, .product__description');
  if (anchor) anchor.parentNode.insertBefore(el, anchor);
  else host.appendChild(el);

  const tabs = [...el.querySelectorAll('.ts-info__tab')];
  const pans = [...el.querySelectorAll('.ts-info__panel')];

  tabs.forEach((t) => t.addEventListener('click', () => {
    tabs.forEach((x) => {
      const on = x === t;
      x.toggleAttribute('data-on', on);
      x.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    pans.forEach((p) => p.toggleAttribute('data-on', p.dataset.p === t.dataset.t));
  }));
}

function init() {
  const h = handleFromUrl();
  if (!h) return;
  const data = SPECS[h];
  if (data) build(data);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => setTimeout(init, 300));
} else {
  setTimeout(init, 300);
}
